import React, { useState, useRef } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 're-resizable';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsProperty } from '../../engine/properties/SettingsProperty';
import { PropertyControl } from './PropertyControl';
import { PropertyGroup } from './PropertyGroup';
import { UIPerformance } from '../core/UIConfig';

type SettingsPanelProps = {
  properties: SettingsProperty<any>[];
  title: string;
  onPropertyChanged: (property: SettingsProperty<any>, newValue: any) => void;
  onClose?: () => void;
  onReset?: () => void;
  onExport?: () => void;
  onImport?: () => void;
  initialPosition?: { x: number, y: number };
  initialSize?: { width: number | string, height: number | string };
  zIndex?: number;
  isVisible?: boolean;
};

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ 
  properties, 
  title, 
  onPropertyChanged, 
  onClose,
  onReset,
  onExport,
  onImport,
  initialPosition = { x: 10, y: 10 },
  initialSize = { width: 380, height: 600 },
  zIndex = 10,
  isVisible = true,
}) => {
  
  if (!isVisible) return null;
  
  const [size, setSize] = useState(initialSize);
  const [activeGroup, setActiveGroup] = useState<string>('');
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const nodeRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Listen for property changes that might affect visibility
  React.useEffect(() => {
    const handlers: (() => void)[] = [];
    
    properties.forEach(property => {
      const handler = () => forceUpdate(); // Force re-render when any property changes
      property.on(handler);
      handlers.push(() => property.off(handler));
    });

    return () => {
      handlers.forEach(cleanup => cleanup());
    };
  }, [properties]);

  // Get performance configuration
  const config = UIPerformance.getConfig();

  // Create property map for visibility condition checking
  const propertyMap = new Map<string, SettingsProperty<any>>();
  properties.forEach(property => {
    if (property && property.name) {
      propertyMap.set(property.name, property);
    }
  });

  // Filter visible properties based on visibility conditions
  const visibleProperties = properties.filter(property => property && property.name && property.isVisible(propertyMap));

  const groupedProperties: { [key: string]: SettingsProperty<any>[] } = {};

  visibleProperties.forEach(property => {
    const group = property.group || 'General';
    if (!groupedProperties[group]) {
      groupedProperties[group] = [];
    }
    groupedProperties[group].push(property);
  });
  
  const groupNames = Object.keys(groupedProperties);

  const scrollToGroup = (groupName: string) => {
    const groupElement = groupRefs.current[groupName];
    const contentElement = contentRef.current;
    const headerElement = headerRef.current;
    
    if (groupElement && contentElement) {
      // Calculate the group's position relative to the content area
      const relativeTop = groupElement.offsetTop;
      
      // Get the actual header height to calculate proper offset
      const headerHeight = headerElement ? headerElement.offsetHeight : 200; // fallback to 200px
      
      // Add extra padding beyond header height to ensure clear visibility
      const extraPadding = 20;
      const totalOffset = headerHeight + extraPadding;
      
      // Calculate the target scroll position - scroll MUCH less to avoid hiding behind header
      const scrollTop = Math.max(0, relativeTop - totalOffset);
      
      contentElement.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
      
      setActiveGroup(groupName);
      
      // Debug info
      console.debug(`Scrolling to "${groupName}": headerHeight=${headerHeight}, relativeTop=${relativeTop}, scrollTop=${scrollTop}`);
    }
  };

  return (
    <AnimatePresence>
      <Draggable 
        nodeRef={nodeRef as React.RefObject<HTMLElement>} 
        handle=".drag-handle" 
        defaultPosition={initialPosition}
        cancel=".no-drag" // Prevent drag on elements with this class
      >
        <div 
          ref={nodeRef}
          data-ui-element="settings-panel"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => {
            // Don't stop propagation for slider elements - they need global cleanup
            const target = e.target as HTMLElement;
            if (!target.closest('[data-slider-element]')) {
              e.stopPropagation();
            }
          }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => {
            // Don't stop propagation for slider elements - they need global cleanup
            const target = e.target as HTMLElement;
            if (!target.closest('[data-slider-element]')) {
              e.stopPropagation();
            }
          }}
          style={{ 
            position: 'absolute', 
            zIndex: zIndex,
            filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5))'
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Resizable
              size={{ width: size.width, height: size.height }}
              onResizeStop={(_e, _direction, _ref, d) => {
                setSize({
                  width: Number(size.width) + d.width,
                  height: Number(size.height) + d.height,
                });
              }}
              minWidth={320}
              minHeight={400}
              style={{
                background: `
                  linear-gradient(135deg, 
                    rgba(15, 15, 35, 0.95) 0%,
                    rgba(25, 15, 45, 0.9) 25%,
                    rgba(35, 25, 55, 0.9) 50%,
                    rgba(25, 15, 45, 0.9) 75%,
                    rgba(15, 15, 35, 0.95) 100%
                  )
                `,
                backdropFilter: config.disableBlurEffects ? 'none' : 'blur(20px) saturate(180%)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Cosmic glow effect - conditionally rendered for performance */}
              {!config.disableCosmicBackgrounds && (
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  right: '-50%',
                  bottom: '-50%',
                  background: `
                    radial-gradient(circle at 30% 20%, rgba(147, 112, 219, 0.15) 0%, transparent 50%),
                    radial-gradient(circle at 70% 80%, rgba(75, 0, 130, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 20% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
                  `,
                  pointerEvents: 'none',
                  zIndex: -1,
                }} />
              )}

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                color: 'white',
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
              }}>
                
                {/* Header */}
                <div 
                  ref={headerRef}
                  className="drag-handle"
                  style={{
                    cursor: 'move',
                    padding: '24px 24px 8px 24px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)',
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  {/* Drag indicator dots */}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '4px',
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: '3px',
                          height: '3px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        }}
                      />
                    ))}
                  </div>

                  <motion.h2 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    style={{
                      margin: '0 0 8px 0',
                      fontSize: '24px',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #E6E6FA 0%, #DDA0DD 50%, #DA70D6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '0.5px',
                      pointerEvents: 'none',
                      zIndex: 2,
                      position: 'relative',
                    }}
                  >
                    {title}
                  </motion.h2>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    style={{
                      margin: '0 0 16px 0',
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontWeight: '400',
                      letterSpacing: '0.3px',
                      pointerEvents: 'none',
                      zIndex: 2,
                      position: 'relative',
                    }}
                  >
                    Cosmic consciousness meets elegant UI configuration
                  </motion.p>

                  {/* Group Navigation Buttons */}
                  {groupNames.length > 1 && (
                    <div
                      className="no-drag"
                      style={{
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        marginBottom: '16px',
                        maxWidth: '100%',
                        zIndex: 10,
                        position: 'relative',
                      }}
                    >
                      {groupNames.map((groupName, index) => (
                        <motion.button
                          key={groupName}
                          whileHover={{ scale: 1.05, backgroundColor: 'rgba(147, 112, 219, 0.2)' }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => scrollToGroup(groupName)}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          style={{
                            background: activeGroup === groupName 
                              ? 'linear-gradient(135deg, rgba(147, 112, 219, 0.3) 0%, rgba(138, 43, 226, 0.2) 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)',
                            border: activeGroup === groupName 
                              ? '1px solid rgba(147, 112, 219, 0.4)' 
                              : '1px solid rgba(255, 255, 255, 0.12)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '500',
                            padding: '6px 10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backdropFilter: config.disableBlurEffects ? 'none' : 'blur(10px)',
                            letterSpacing: '0.3px',
                            textShadow: activeGroup === groupName && !config.disableGlowEffects 
                              ? '0 0 8px rgba(147, 112, 219, 0.6)' 
                              : 'none',
                            boxShadow: activeGroup === groupName && !config.disableGlowEffects
                              ? '0 2px 12px rgba(147, 112, 219, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                              : '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                            pointerEvents: 'auto',
                            zIndex: 10,
                          }}
                        >
                          {groupName}
                        </motion.button>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="no-drag"
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      zIndex: 10,
                      position: 'relative',
                    }}
                  >
                    {onReset && (
                      <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'rgba(220, 20, 60, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onReset}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                          ...actionButtonStyle('#DC143C', config.disableGlowEffects),
                          pointerEvents: 'auto',
                          zIndex: 10,
                        }}
                      >
                        ↻ Reset to Defaults
                      </motion.button>
                    )}
                    
                    {onExport && (
                      <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'rgba(70, 130, 180, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onExport}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                          ...actionButtonStyle('#4682B4', config.disableGlowEffects),
                          pointerEvents: 'auto',
                          zIndex: 10,
                        }}
                      >
                        ↓ Export Settings
                      </motion.button>
                    )}
                    
                    {onImport && (
                      <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'rgba(60, 179, 113, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onImport}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                          ...actionButtonStyle('#3CB371', config.disableGlowEffects),
                          pointerEvents: 'auto',
                          zIndex: 10,
                        }}
                      >
                        ↑ Import Settings
                      </motion.button>
                    )}
                  </motion.div>

                  {onClose && (
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={onClose}
                      onMouseDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                      }}
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '18px',
                        cursor: 'pointer',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        zIndex: 20,
                        pointerEvents: 'auto',
                      }}
                    >
                      ✕
                    </motion.button>
                  )}
                  
                  {/* Drag instruction text */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    style={{
                      fontSize: '10px',
                      color: 'rgba(255, 255, 255, 0.4)',
                      marginTop: '12px',
                      fontStyle: 'italic',
                      pointerEvents: 'none',
                      zIndex: 2,
                      position: 'relative',
                    }}
                  >
                    ⇅ Drag header to move
                  </motion.div>
                </div>

                {/* Content */}
                <div 
                  ref={contentRef}
                  style={{
                    padding: '20px 24px 24px 24px',
                    flexGrow: 1,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
                  }}
                >
                  <style>
                    {`
                      /* Custom scrollbar */
                      div::-webkit-scrollbar {
                        width: 6px;
                      }
                      div::-webkit-scrollbar-track {
                        background: rgba(255, 255, 255, 0.05);
                        border-radius: 3px;
                      }
                      div::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.3);
                        border-radius: 3px;
                      }
                      div::-webkit-scrollbar-thumb:hover {
                        background: rgba(255, 255, 255, 0.4);
                      }
                    `}
                  </style>

                  {Object.keys(groupedProperties).length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4, staggerChildren: 0.1 }}
                    >
                      {Object.entries(groupedProperties).map(([groupName, props], index) => (
                        <motion.div
                          key={groupName}
                          ref={(el) => { groupRefs.current[groupName] = el; }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          style={{
                            marginBottom: '16px',
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            overflow: 'hidden',
                          }}
                        >
                          <PropertyGroup title={groupName} startOpen>
                            {props.map((property) => (
                              <PropertyControl 
                                key={property.id} 
                                property={property} 
                                onPropertyChange={onPropertyChanged}
                              />
                            ))}
                          </PropertyGroup>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{
                        textAlign: 'center',
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '14px',
                        marginTop: '40px',
                      }}
                    >
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌌</div>
                      <p>No properties to configure in this cosmic realm.</p>
                    </motion.div>
                  )}
                </div>
              </div>
            </Resizable>
          </motion.div>
        </div>
      </Draggable>
    </AnimatePresence>
  );
};

const actionButtonStyle = (accentColor: string, disableGlow?: boolean): React.CSSProperties => ({
  background: `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)`,
  border: `1px solid rgba(255, 255, 255, 0.15)`,
  borderRadius: '8px',
  color: 'white',
  fontSize: '11px',
  fontWeight: '500',
  padding: '8px 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backdropFilter: 'blur(10px)',
  letterSpacing: '0.3px',
  textShadow: disableGlow ? 'none' : `0 0 10px ${accentColor}40`,
  boxShadow: disableGlow 
    ? '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
    : `0 4px 12px ${accentColor}30, inset 0 1px 0 rgba(255, 255, 255, 0.15)`,
});
